import { useToast } from "@/hooks/use-toast"
import { Member } from "../../types/memberTypes"
import {
  removeUserFromPortfolio,
  removeUserFromBusinesses,
} from "@/utils/firestore/users/portfolioMembership"
import { removeUserFromBusiness } from "@/utils/firestore/business/businessMembership"
import {
  addUser,
  resendInvitation,
  updateInvitationStatus
} from "@/services/auth/userManagement"

export const useMemberRemoval = (
  setMembers: React.Dispatch<React.SetStateAction<Member[]>>,
  source: "portfolio" | "business" = "portfolio",
  entityId?: string
) => {
  const { toast } = useToast()

  // Remove member actions
  const handleRemoveMemberClick = (
    member: Member,
    setMemberToDelete: (member: Member | null) => void,
    setShowDeleteMemberDialog: (show: boolean) => void,
    setShowPrimaryOwnerDialog: (show: boolean) => void
  ) => {
    if (member.role === "Primary Owner") {
      setShowPrimaryOwnerDialog(true)
      return
    }

    // Set member to delete and show confirmation dialog
    setMemberToDelete(member)
    setShowDeleteMemberDialog(true)
  }

  // Handle current member removal (leaving the portfolio/business)
  const handleRemoveCurrentUser = (
    members: Member[],
    setShowPrimaryOwnerDialog: (show: boolean) => void,
    setShowLeavePortfolioDialog: (show: boolean) => void
  ) => {
    const currentMember = members.find((m) => m.isCurrentUser)

    if (!currentMember) {
      toast({
        title: "Error",
        description: "Current user not found in members list.",
      })
      return
    }

    if (currentMember.role === "Primary Owner") {
      // Primary owner cannot leave
      setShowPrimaryOwnerDialog(true)
      return
    }

    // Show leave confirmation dialog
    setShowLeavePortfolioDialog(true)
  }

  const handleConfirmDelete = async (
    memberToDelete: Member | null,
    setMemberToDelete: (member: Member | null) => void,
    setShowDeleteMemberDialog: (show: boolean) => void
  ) => {
    if (!memberToDelete) return

    try {
      // 1. Remove from portfolio or business
      if (source === "portfolio" && entityId) {
        const portfolioRemoved = await removeUserFromPortfolio(
          memberToDelete.userId || "",
          entityId
        )

        if (!portfolioRemoved) {
          throw new Error("Failed to remove from portfolio")
        }

        // 2. Remove from businesses linked to this portfolio
        await removeUserFromBusinesses(memberToDelete.userId || "", entityId)
      } else if (source === "business" && entityId) {
        // If source is business, remove from this specific business
        const businessRemoved = await removeUserFromBusiness(
          memberToDelete.userId || "",
          entityId
        )

        if (!businessRemoved) {
          throw new Error("Failed to remove from business")
        }
      } else {
        throw new Error("Invalid source or missing entity ID")
      }

      // 3. Update invitation status to "removed" if email exists
      if (memberToDelete.email) {
        await updateInvitationStatus(memberToDelete.email, "removed")
      }

      // 4. Update local state only after successful API operations
      setMembers((prev) => prev.filter((m) => m.id !== memberToDelete?.id))

      // 5. Show success message
      toast({
        title: "Member Removed",
        description: `${memberToDelete.firstName} ${memberToDelete.lastName} has been removed from this ${source}.`,
      })

      // 6. Close dialog
      setShowDeleteMemberDialog(false)
      setMemberToDelete(null)
    } catch (error) {
      // Handle errors
      console.error("Error removing member:", error)
      toast({
        title: "Error",
        description: `Failed to remove member: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        variant: "destructive",
      })
    }
  }

  const handleLeavePortfolio = async () => {
    try {
      // Find current user
      const currentMember = await new Promise<Member | undefined>((resolve) => {
        setMembers((prev) => {
          const member = prev.find((m) => m.isCurrentUser)
          resolve(member)
          return prev
        })
      })

      if (!currentMember || !entityId) {
        toast({
          title: "Error",
          description: "Current user not found or entity ID missing.",
          variant: "destructive",
        })
        return
      }

      // 1. Remove from portfolio or business
      if (source === "portfolio") {
        const portfolioRemoved = await removeUserFromPortfolio(
          currentMember.userId || "",
          entityId
        )

        if (!portfolioRemoved) {
          throw new Error(`Failed to leave ${source}`)
        }

        // 2. Remove from businesses linked to this portfolio
        await removeUserFromBusinesses(currentMember.userId || "", entityId)
      } else if (source === "business") {
        // Business-specific removal
        const businessRemoved = await removeUserFromBusiness(
          currentMember.userId || "",
          entityId
        )

        if (!businessRemoved) {
          throw new Error(`Failed to leave ${source}`)
        }
      } else {
        throw new Error("Invalid source or missing entity ID")
      }

      // 3. Update local state
      setMembers((prev) => prev.filter((m) => !m.isCurrentUser))

      // 4. Show success message
      toast({
        title: `Left ${source === "portfolio" ? "Portfolio" : "Business"}`,
        description: `You have successfully left this ${source}.`,
      })
    } catch (error) {
      // Handle errors
      console.error(`Error leaving ${source}:`, error)
      toast({
        title: "Error",
        description: `Failed to leave ${source}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        variant: "destructive",
      })
    }
  }

  const handleResendInvite = async (member: Member) => {
    try {
      if (!entityId) {
        toast({
          title: "Error",
          description: "Entity ID is missing.",
          variant: "destructive",
        })
        return
      }

      // Call the resendInvitation function
      const result = await resendInvitation(
        {
          email: member.email,
          firstName: member.firstName,
          lastName: member.lastName,
          role: member.role,
          phone: member.phone,
          permissions: member.permissions,
        },
        entityId
      )

      if (result.success) {
        // Don't update the UI state since the backend will handle updating the invitation
        // and we don't want to create a new row
        toast({
          title: "Invitation Resent",
          description: `An invitation has been resent to ${member.firstName} ${member.lastName}.`,
        })

        // Important: We're not updating the members state here to avoid creating a new row
        // The invitation is updated in the database, but we don't need to refresh the UI
      } else {
        throw new Error(result.error || "Failed to resend invitation")
      }
    } catch (error) {
      console.error("Error resending invitation:", error)
      toast({
        title: "Error",
        description: `Failed to resend invitation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        variant: "destructive",
      })
    }
  }

  return {
    handleRemoveMemberClick,
    handleRemoveCurrentUser,
    handleConfirmDelete,
    handleLeavePortfolio,
    handleResendInvite,
  }
}
